import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { FondDeProjet, FormeDeProjet, TraitDeProjet } from '@nextilt/commun';
import type { Bulle, BulleEnrichie, Etat, Feuille, Priorite } from '../partage/api/types';
import { cles } from '../partage/relectures';
import { useFeuilles } from '../partage/useArbre';
import { arbreVisible, deplieesVisibles } from './arbreDeLaHierarchie';
import { dansLeCalque, couleurDAxe, MOLETTE_APAISEE_MS, type Calque, type ProfondeurIci } from '../carte';
import { gestesDuCentre } from '../partage/deuxGestes';
import { NoeudHierarchie } from './NoeudHierarchie';
import { courbeHorizontale, LARGEUR_CENTRE, placerLaHierarchie } from './placeDeLaHierarchie';
import { GESTE_DU_CENTRE, motDuCalque } from '../partage/vocabulaire';
import { teinteOuAccent } from '../apparence';
import type { BulleAHabiller, HabitDessine } from '../partage/objetsDeBulle';
import type { HabitDuProjetPourUneBulle } from '../fiche';
import { loupeSurLaBulle, SANS_LOUPE, type LoupeDeLaCarte } from '../partage/loupeDeLaCarte';
import './VueHierarchie.css';

/**
 * La vue « Hiérarchie » (ticket 35) : la feuille en arbre
 * horizontal, le dossier à gauche, ses étapes de haut en bas **dans l'ordre où tu t'y
 * prends** (ton rang, puis l'arrivée, calculé par le serveur). On lit le cheminement d'un
 * coup d'œil, sans chercher où est le 1 et où est le 5.
 *
 * Une flèche, ou la molette sur une carte (vers l'avant elle s'ouvre, vers l'arrière elle se
 * referme), déplie ou replie les bulles d'une étape ; au départ, seul le premier niveau l'est.
 * Le sélecteur de profondeur ouvre tout un niveau d'un coup (ticket 13 de retours-testeurs,
 * qui remplace « Tout déplier » du ticket 07). Ce qui est déplié est le même état que sur la
 * carte, tenu par la feuille le temps du dossier (`profondeur.ts`) : aucune position n'est
 * stockée (interdit n° 2), l'arbre se recalcule à chaque rendu.
 *
 * On hiérarchise d'ici comme ailleurs : le bandeau reste en haut, on désigne les bulles de la
 * feuille dans l'ordre, et à l'enregistrement elles glissent à leur nouvelle place.
 *
 * Le calque de priorité choisi dans ⚙ vaut ici comme en Carte (ticket 04 du chantier
 * `priorites`) : ce qui n'est pas la priorité mise en avant s'efface, rien ne bouge.
 *
 * Ce fichier tient ce qui dépend de l'écran : ce qui est déplié, la hauteur que chaque carte
 * a prise, l'échelle. Ce qui est visible se calcule dans `arbreDeLaHierarchie.ts`, la place
 * de chaque carte dans `placeDeLaHierarchie.ts`.
 */
export function VueHierarchie({
  centre,
  enfants,
  ordre,
  axes,
  calque,
  enAvant,
  fond,
  forme,
  trait,
  habitDe,
  habitDuProjet,
  hierarchisation,
  profondeur,
  onEntrer,
  onFiche,
  onEtat,
  onDesigner,
  loupe = SANS_LOUPE,
}: {
  centre: Bulle;
  enfants: BulleEnrichie[];
  /** Les enfants de haut en bas, tels que le serveur les ordonne. */
  ordre: string[];
  /**
   * L'axe de chaque bulle sous la feuille, celui de la carte (`carte.axes`) : chaque carte de
   * l'arbre prend la couleur de son axe, la même qu'en Carte (ticket 11 de retours-testeurs).
   */
  axes: Record<string, number>;
  /** Le calque de la carte (`carte.calque`) et la priorité mise en avant, ou aucune. */
  calque: Calque;
  enAvant: Priorite | null;
  /** Le fond de la carte, celui de l'habit du projet (Uni : le fond de l'ambiance). */
  fond: FondDeProjet;
  /** La forme des cartes, celle de l'habit du projet ; aucune au téléphone (`formeDessinee`). */
  forme: FormeDeProjet | undefined;
  /** Le trait de leur contour, celui de l'habit du projet ; aucun au téléphone (`traitDessine`). */
  trait: TraitDeProjet | undefined;
  /**
   * L'habit de chaque carte, par-dessus celui du projet (chantier habit-des-bulles, modification
   * 03, `habitsDessines`), avec les bulles des feuilles dépliées pour la couleur de leur branche.
   */
  habitDe: (bulle: BulleAHabiller, lire?: (id: string) => BulleAHabiller | undefined) => HabitDessine;
  /** L'habit du projet, pour le menu « ⋯ » de chaque bulle (habit-des-bulles, 05) ; `null` au téléphone. */
  habitDuProjet: HabitDuProjetPourUneBulle | null;
  /** Les numéros posés pendant qu'on hiérarchise. `null` quand le mode est éteint. */
  hierarchisation: Map<string, number> | null;
  profondeur: ProfondeurIci;
  onEntrer: (id: string) => void;
  /** Une bulle dépliée comprise : sa fiche trouve sa feuille dans ce qui est lu (`laFicheOuverte`). */
  onFiche: (id: string) => void;
  onEtat: (id: string, etat: Etat) => void;
  onDesigner: (id: string) => void;
  /** Les touches de la carte (chantier modes-de-la-carte, modification 02) : infos et recherche. */
  loupe?: LoupeDeLaCarte;
}) {
  // Le centre est toujours déplié : on est dedans, ses bulles sont le premier niveau.
  const ouvertes = useMemo(
    () => new Set([centre.id, ...profondeur.ouvertes]),
    [centre.id, profondeur.ouvertes]
  );
  const { ouvrir, fermer } = profondeur;
  const enHierarchisation = hierarchisation !== null;

  /*
   * Ce qui est visible, d'après les feuilles déjà lues ; les feuilles des étapes dépliées
   * et visibles se lisent (ou se relisent) ensuite, et la vue se redessine à leur arrivée.
   */
  const client = useQueryClient();
  const { arbre, visibles } = arbreVisible(
    centre.id,
    (id) => {
      if (id === centre.id) return { enfants, ordre };
      const feuille = client.getQueryData<Feuille>(cles.feuille(id));
      return feuille ? { enfants: feuille.enfants, ordre: feuille.ordreHierarchique } : undefined;
    },
    ouvertes
  );
  useFeuilles(deplieesVisibles(centre.id, visibles, ouvertes));

  // La hauteur de chaque carte, lue à l'écran : un titre long allonge la carte.
  const [hauteurs, setHauteurs] = useState<Map<string, number>>(() => new Map());
  const [montrees, setMontrees] = useState<Set<string>>(() => new Set());
  const elements = useRef(new Map<string, HTMLDivElement>());
  const rappels = useRef(new Map<string, (element: HTMLDivElement | null) => void>());
  const mesure = (id: string) => {
    let rappel = rappels.current.get(id);
    if (!rappel) {
      rappel = (element) => {
        if (element) elements.current.set(id, element);
        else elements.current.delete(id);
      };
      rappels.current.set(id, rappel);
    }
    return rappel;
  };

  const { places, traits, largeur, hauteur } = placerLaHierarchie(arbre, hauteurs);

  useLayoutEffect(() => {
    let change = false;
    const lues = new Map(hauteurs);
    for (const [id, element] of elements.current) {
      const h = element.offsetHeight;
      if (lues.get(id) !== h) {
        lues.set(id, h);
        change = true;
      }
    }
    if (change) {
      setHauteurs(lues);
      return;
    }
    /*
     * Une carte qui vient d'apparaître a d'abord été posée à une hauteur supposée, puis à la
     * sienne, sans glisser. Elle ne rejoint les autres (celles qui glissent) qu'une fois
     * posée à sa vraie place : la lecture des hauteurs ci-dessus a fait calculer le style à
     * cette place-là, donc elle apparaît en fondu sans glisser depuis la place supposée.
     * Pas d'attente d'image (`requestAnimationFrame`) : une fenêtre cachée n'en donne pas, et
     * les cartes resteraient invisibles.
     */
    const neuves = [...places.keys()].filter((id) => !montrees.has(id));
    if (neuves.length > 0) setMontrees((avant) => new Set([...avant, ...neuves]));
  });

  // L'arbre se recadre pour tenir à l'écran, jusqu'à un plancher où il se lit encore ; au-delà,
  // il défile. Le premier cadrage se pose sans animation.
  const surface = useRef<HTMLDivElement>(null);
  const [dispo, setDispo] = useState<{ l: number; h: number } | null>(null);
  const [cadree, setCadree] = useState(false);
  useEffect(() => {
    const element = surface.current;
    if (!element) return;
    const lire = () => {
      const r = element.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setDispo({ l: r.width, h: r.height });
    };
    lire();
    const observateur = new ResizeObserver(lire);
    observateur.observe(element);
    return () => observateur.disconnect();
  }, []);
  useEffect(() => {
    if (!dispo || cadree) return;
    // Un court délai plutôt qu'une image : une fenêtre cachée n'en donne pas.
    const minuteur = setTimeout(() => setCadree(true), 60);
    return () => clearTimeout(minuteur);
  }, [dispo, cadree]);
  const facteur = dispo
    ? Math.min(
        1,
        Math.max(
          PLANCHER,
          Math.min((dispo.l - 2 * MARGE) / largeur, (dispo.h - 2 * MARGE) / Math.max(hauteur, 1))
        )
      )
    : 1;

  /** Replier une étape replie aussi ce qu'elle contenait (`profondeur.ts`). */
  const basculer = (id: string) => (ouvertes.has(id) ? fermer(id) : ouvrir(id));

  /*
   * La molette sur une carte qui contient quelque chose (décision d'Igor du 2026-09-27) : vers
   * l'avant elle s'ouvre, vers l'arrière elle se referme, un cran à la fois, comme sur la
   * carte. Ailleurs, et sur une carte vide, la molette fait défiler l'arbre.
   */
  const molette = useRef<(evenement: WheelEvent) => void>(() => {});
  const derniereMolette = useRef(0);
  molette.current = (evenement) => {
    if (enHierarchisation) return;
    const noeud = (evenement.target as Element).closest<HTMLElement>('.hier-noeud[data-bulle]');
    const id = noeud?.dataset.bulle;
    if (!id || !visibles.get(id)?.bulle.nbEnfants) return;
    evenement.preventDefault();
    const maintenant = Date.now();
    if (maintenant - derniereMolette.current < MOLETTE_APAISEE_MS) return;
    const ouverte = ouvertes.has(id);
    if (evenement.deltaY < 0 && !ouverte) ouvrir(id);
    else if (evenement.deltaY > 0 && ouverte) fermer(id);
    else return;
    derniereMolette.current = maintenant;
  };
  useEffect(() => {
    const element = surface.current;
    if (!element) return;
    const surMolette = (evenement: WheelEvent) => molette.current(evenement);
    element.addEventListener('wheel', surMolette, { passive: false });
    return () => element.removeEventListener('wheel', surMolette);
  }, []);

  const placeCentre = places.get(centre.id);

  return (
    <div className="hierarchie-tout">
      {/* Le fond de la carte, réglé dans l'habit du projet (chantier habit-du-projet, ticket 02,
          `fonds/papiers.css`) : il défile avec l'arbre, à sa taille, quelle que soit l'échelle. La
          forme de ses cartes aussi (ticket 03, `styles/bulles/`), et le trait de leur contour (ticket
          04, `styles/bulles/trait*.css`). */}
      <div
        className="hierarchie fond-de-projet forme-de-projet trait-de-projet"
        data-fond={fond}
        data-forme={forme}
        data-trait={trait}
        ref={surface}
      >
        <div
          className={`hierarchie-cadre${cadree ? '' : ' hierarchie--direct'}`}
          style={{ width: largeur * facteur + 2 * MARGE, height: hauteur * facteur + 2 * MARGE }}
        >
          <div
            className={`hierarchie-plan${cadree ? '' : ' hierarchie--direct'}`}
            style={{
              width: largeur,
              height: hauteur,
              transform: `translate(${MARGE}px, ${MARGE}px) scale(${facteur})`,
            }}
          >
            <svg className="hierarchie-traits" width={largeur} height={hauteur} aria-hidden="true">
              {traits.map((trait) => {
                const d = courbeHorizontale(trait.de, trait.a);
                return (
                  <path
                    key={trait.vers}
                    className={`trait-vers-la-mere${montrees.has(trait.vers) ? '' : ' hierarchie-trait--neuf'}`}
                    d={d}
                    /* La propriété CSS fait glisser le trait avec les cartes ; l'attribut
                       le dessine là où elle n'est pas comprise. Son épaisseur suit le trait
                       de l'habit du projet (`styles/bulles/trait.css`). */
                    style={{ d: `path('${d}')`, ['--epaisseur-du-lien' as string]: EPAISSEUR_DU_LIEN } as CSSProperties}
                    stroke="var(--accent-pale)"
                    strokeWidth={EPAISSEUR_DU_LIEN}
                    fill="none"
                  />
                );
              })}
            </svg>

            {placeCentre && (
              <div
                ref={mesure(centre.id)}
                className="hier-centre"
                data-forme={forme}
                style={{ left: placeCentre.x, top: placeCentre.y, width: LARGEUR_CENTRE }}
              >
                <span className="hier-centre-teinte" aria-hidden="true" />
                {/* On est déjà dedans : un clic ne fait rien, un double-clic ouvre sa fiche,
                    comme sur la carte (retour 3 des testeurs, `gestesDuCentre`). */}
                <button
                  type="button"
                  className="hier-centre-titre"
                  {...gestesDuCentre(() => onFiche(centre.id))}
                  title={GESTE_DU_CENTRE}
                >
                  {centre.titre}
                </button>
              </div>
            )}

            {[...visibles.entries()].map(([id, { bulle, niveau }]) => {
              const place = places.get(id);
              if (!place) return null;
              const designable = niveau === 1;
              const dans = dansLeCalque(bulle, enAvant, calque);
              const habit = habitDe(bulle, (autre) => visibles.get(autre)?.bulle);
              return (
                <NoeudHierarchie
                  key={id}
                  bulle={bulle}
                  place={place}
                  axe={habit.couleur ? teinteOuAccent(habit.couleur) : couleurDAxe(axes, id)}
                  habit={habit}
                  habitDuProjet={habitDuProjet}
                  dedans={bulle.parentId !== centre.id}
                  deplie={ouvertes.has(id)}
                  neuf={!montrees.has(id)}
                  mesure={mesure(id)}
                  enHierarchisation={enHierarchisation}
                  designable={designable}
                  rangEnAttente={designable ? (hierarchisation?.get(id) ?? null) : null}
                  horsDuCalque={!dans.allumee}
                  combienDansLeCalque={enAvant && dans.combien ? { priorite: enAvant, combien: dans.combien } : null}
                  loupe={loupeSurLaBulle(loupe, bulle)}
                  onEntrer={onEntrer}
                  onFiche={onFiche}
                  onEtat={onEtat}
                  onDesigner={onDesigner}
                  onDeplier={basculer}
                />
              );
            })}
          </div>
        </div>
      </div>

      {enfants.length > 0 && (
        <p className="hierarchie-aide">
          Clique <b>›</b> ou tourne <b>la molette vers l’avant</b> sur une étape pour la déplier,{' '}
          <b>‹</b> ou vers l’arrière pour la replier
          {enAvant && <> · <b>{motDuCalque(enAvant)}</b></>}
        </p>
      )}
    </div>
  );
}

/** Sous ce facteur, un titre cesse de se lire : l'arbre défile plutôt que de rapetisser. */
const PLANCHER = 0.75;
/** Le blanc autour de l'arbre, à l'écran. */
const MARGE = 24;
/** L'épaisseur d'un trait vers la mère au Crayon ; les autres traits la multiplient (`trait-*.css`). */
const EPAISSEUR_DU_LIEN = 1.6;
